<template>
  <ValidationProvider
    tag="div"
    :rules="selectRules"
    :name="vid"
    :label="label || name"
    :mode="mode"
    :model-value="modelValue"
    :validate-on-mount="false"
    :validate-on-input="false"
    :validate-on-model-update="false"
    v-slot="{ errors, validate }"
  >
    <b-form-group :label-for="name || labelText" :label="labelText" :class="{ 'mb-0' : compactFormat}" >
      <b-input-group>
      <b-form-select
        v-bind="$attrs"
        :name="name"
        :model-value="value"
        :options="filteredOptions"
        @change="handleChange($event)"
        @update:model-value="(val) => handleSelection(val, validate)"
        :class="{ 'is-invalid': errors.length, 'compact-control' : compactFormat,  'py-0' : compactFormat}"
      >
        <template v-slot:first v-if="noSelectionText">
          <b-form-select-option :value="null" :disabled="noSelectionIsPlaceholderOnly" :hidden="noSelectionIsPlaceholderOnly">{{noSelectionText}}</b-form-select-option>
        </template>
     </b-form-select>
        <template v-slot:append v-if="infoMessage">
            <b-input-group-text class="px-1 info-append"><b-icon-info-circle v-b-tooltip="{
                title: infoMessage,
                trigger: 'hover',
                placement: 'auto',
                boundary: 'viewport',
                container: 'body',
                teleportTo: 'body',
                customClass: 'p-info-tooltip'
            }"></b-icon-info-circle></b-input-group-text>
        </template>
        <template v-slot:append v-else-if="$slots.append">
            <slot name="append" />
        </template>
        </b-input-group>
        <slot name="error-message" v-if="errors && errors.length > 0">
          <!-- allow for easy way to set a custom error message -->
          <span class="text-danger">{{ errors[0] }}</span>
        </slot>
    </b-form-group>
  </ValidationProvider>
</template>

<script>
import selectListOptionsDataContext from "@/services/selectListOptions.dataContext.js";
import baseInputs from "@/components/mixins/BaseInputs";
import validatedInput from "@/components/mixins/ValidatedInput";
import { useSelectListsStore } from '@/stores/selectLists.js';

export default {
  name: "p-select",
  mixins: [baseInputs, validatedInput],
  emits: ['change', 'filter', 'selectedObject', 'update:modelValue', 'input'],
  props: {
    // value prop now comes from modelValue via BaseInputs mixin
    // When true, the noSelectionMessage option is shown only as the placeholder
    // (disabled + hidden) — it can't be re-selected from the list.
    noSelectionDisabled: Boolean,
    dataType: String,
    options: Array,
    filter: [String,Number,Function],
    addEmptyOption:Boolean,
    readOnly: Boolean,
    compactFormat: Boolean,
    setInvalidValueToFirstOption: {
        type: Boolean,
        default: false
    },
    emptyOnNullFilter: {
        type: Boolean,
        default: false
    },
    addInvalidOption: {
        type: Object,
        default: null
    },
    addNullOption: {
        type: String,
        default: null
    },
    infoMessage: String
  },
  data() {
    return {
      innerOptions: [],
      hasUserInteracted: false
    };
  },
  async created() {
    if (this.dataType) {
      // Triggers the fetch (which writes the result into the Pinia store).
      // Reactivity from the Pinia state then updates `innerOptions` via the
      // pinedOptions watcher — no more manual subscribe/unsubscribe.
      this.innerOptions = (await this.loadList(this.dataType)) || [];
    } else {
      this.innerOptions = this.options || [];
    }
    this.setInnerOptionsToObjects();
  },
  computed: {
    value() { return this.modelValue; },
    // Placeholder text for the unselected (null) option. Defaults to
    // "Select a {Label}" when none is supplied. An explicit noSelectionMessage
    // (even '') always wins; addEmptyOption already provides a blank
    // "no selection" choice, so we don't add a second one on top of it.
    noSelectionText() {
      if (this.noSelectionMessage != null) return this.noSelectionMessage;
      if (this.addEmptyOption) return null;
      return this.label ? `Select a ${this.label}` : null;
    },
    // The auto "Select a {Label}" default is a prompt only — never a real
    // choice in the list. Explicit messages (e.g. "Show All") stay selectable
    // unless the caller opts into noSelectionDisabled.
    noSelectionIsPlaceholderOnly() {
      return this.noSelectionDisabled || this.noSelectionMessage == null;
    },
    // Reactive pointer at the Pinia state for this dataType. When the
    // dataContext or SignalR replaces the array, this re-evaluates and
    // the watcher below copies it onto innerOptions.
    pinedOptions() {
      if (!this.dataType) return null;
      return useSelectListsStore().lists[this.dataType];
    },
    filteredOptions(){
        let filteredOptions = this.innerOptions || [];
        if(this.filter || this.emptyOnNullFilter){

          if((typeof this.filter == 'function')){
            filteredOptions = filteredOptions.filter(x => this.filter(x));
          }
          else{
            filteredOptions = filteredOptions.filter(x => x.filter == this.filter);
          }
        }
        if(this.addInvalidOption){
          filteredOptions = [this.addInvalidOption, ...filteredOptions]
        }
        if(this.addEmptyOption){
          // Show the "Select a {label}" prompt as the blank option's text instead
          // of an empty row (still selectable to clear). Falls back to '' if unlabeled.
          return [{value:null, text: this.selectPrompt ?? ''}, ... filteredOptions]
        }
        if(this.addNullOption){
          filteredOptions = [{value:null, text: this.addNullOption}, ... filteredOptions]
        }
        return filteredOptions;
    },
  },
  watch: {
      options: {
      handler: function() {
          this.innerOptions = this.options || [];
          this.setInnerOptionsToObjects();
      }
    },
    pinedOptions: {
      handler: function(newVal) {
          if (newVal && newVal.length > 0) {
              this.innerOptions = newVal;
              this.setInnerOptionsToObjects();
          }
      }
    },
    filteredOptions: {
      handler: function() {
        //make sure inneroptions isn't empty, so it doesn't clear the value before options are loaded.
        //skip clearing until the user has interacted, so async options loading on mount doesn't
        //mutate the parent model and flip the navigation guard's dirty flag.
        if(this.filteredOptions && this.innerOptions.length > 0 && !this.filteredOptions.some( x=> x.value == this.value)){
          if(this.setInvalidValueToFirstOption && this.filteredOptions.length > 0){
            this.handleSelection(this.filteredOptions[0].value);
          }
          else if(this.hasUserInteracted){
            this.handleInput(null);
          }
        }
      }
    }
  },
  methods: {
    setInnerOptionsToObjects(){
      if (!this.innerOptions) { this.innerOptions = []; }
      //to match bootstrap selects
      //If option array is a list of strings, it will be used for both the generated value and text fields.
      if(this.innerOptions.length > 0 && typeof(this.innerOptions[0]) === "string"){
          this.innerOptions = this.innerOptions.map((x) => ({text: x, value:x}));
      }
    },
    async loadList(dataType) { 
      return await selectListOptionsDataContext.getStoreDropdownData(dataType);
    },
    handleSelection(selectionValue, validate){
      this.hasUserInteracted = true;
      this.handleInput(selectionValue);

      var option = this.innerOptions.find(x => x.value == selectionValue) || {};

      this.$emit("filter", option.filter);
      this.$emit("selectedObject", option);
      this.$emit('change', selectionValue);

      // Re-validate on user selection so a required error clears once a value is
      // picked. nextTick lets the v-model round-trip update the field value
      // first. Only runs for real user selections (validate is undefined for
      // programmatic calls like option-load defaulting), so we don't validate
      // prematurely on load.
      if (validate) {
        this.$nextTick(() => validate());
      }
    },
    handleChange(){
        // Native @change on b-form-select passes a DOM Event, not the value.
        // handleSelection (from @update:model-value) already emits 'change' with the proper value.
        this.hasUserInteracted = true;
    }
  }
};
</script>


<style scoped lang="scss">
:deep(.compact-control ) {
    padding-bottom: 5px;
    padding-top: 8px;
    padding-left: 2px;
    height: 1.5rem;
}
/* Info-icon append: drop the grey input-group-text box so the icon floats
   cleanly next to the field. */
:deep(.info-append) {
    background-color: transparent;
    border: none;
}
</style>

<!-- Unscoped: tooltip lives outside our component scope. Give all tooltips a
     sane width and override the modal's overflow:hidden constraint so info
     tooltips inside modals can render at full width above the icon. -->
<style lang="scss">
.tooltip,
.p-info-tooltip {
    max-width: 320px !important;
    z-index: 2000 !important;
}
.tooltip .tooltip-inner,
.p-info-tooltip .tooltip-inner {
    max-width: 320px !important;
    text-align: left !important;
    white-space: normal !important;
}
</style>

